/* oxlint-disable no-fallthrough -- intentional fallthrough in hash algorithm */
export const hashString = (str: string, salt = '') =>
  murmurhash2_64_gc(str + salt)
    .toString(16)
    .padStart(16, '0')
export const hashString128 = (str: string, salt = '') =>
  murmurhash2_128_gc(str + salt)
export const hashStringDigits = (str: string, salt = '') => {
  const hash = murmurhash2_128_gc(str + salt)
  let digits = ''
  for (let i = 0; i < hash.length; i++) {
    const hexChar = hash[i]
    const value = Number.parseInt(hexChar, 16)
    digits += value.toString()
  }
  while (digits.length < 16) {
    const additionalHash = murmurhash2_64_gc(digits + str + salt).toString()
    digits += additionalHash
  }
  return digits.slice(0, Math.max(16, digits.length))
}

function murmurhash2_64_gc(str: string, seed = 0): bigint {
  const m = 0xc6a4a7935bd1e995n
  const r = BigInt(47)

  let h = BigInt(seed) ^ (BigInt(str.length) * m)

  const length = str.length
  const remainder = length & 7
  const bytes = length - remainder

  let i = 0
  while (i < bytes) {
    let k =
      BigInt((str.codePointAt(i++) ?? 0) & 0xff) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(8)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(16)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(24)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(32)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(40)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(48)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(56))

    k = k * m
    k ^= k >> r
    k = k * m

    h ^= k
    h = h * m
  }

  switch (remainder) {
    case 7:
      h ^= BigInt((str.codePointAt(i + 6) ?? 0) & 0xff) << BigInt(48)
    case 6:
      h ^= BigInt((str.codePointAt(i + 5) ?? 0) & 0xff) << BigInt(40)
    case 5:
      h ^= BigInt((str.codePointAt(i + 4) ?? 0) & 0xff) << BigInt(32)
    case 4:
      h ^= BigInt((str.codePointAt(i + 3) ?? 0) & 0xff) << BigInt(24)
    case 3:
      h ^= BigInt((str.codePointAt(i + 2) ?? 0) & 0xff) << BigInt(16)
    case 2:
      h ^= BigInt((str.codePointAt(i + 1) ?? 0) & 0xff) << BigInt(8)
    case 1:
      h ^= BigInt((str.codePointAt(i) ?? 0) & 0xff)
      h = h * m
  }

  h ^= h >> r
  h = h * m
  h ^= h >> r

  return h & BigInt('0xFFFFFFFFFFFFFFFF')
}

function murmurhash2_128_gc(str: string, seed = 0): string {
  const m = 0xc6a4a7935bd1e995n
  const r = BigInt(47)

  let h1 = BigInt(seed) ^ (BigInt(str.length) * m)
  let h2 = BigInt(seed + 1) ^ (BigInt(str.length) * m)

  const length = str.length
  const remainder = length & 7
  const bytes = length - remainder

  let i = 0
  while (i < bytes) {
    let k1 =
      BigInt((str.codePointAt(i++) ?? 0) & 0xff) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(8)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(16)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(24)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(32)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(40)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(48)) |
      (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(56))

    k1 = k1 * m
    k1 ^= k1 >> r
    k1 = k1 * m

    h1 ^= k1
    h1 = h1 * m

    if (i < bytes) {
      let k2 =
        BigInt((str.codePointAt(i++) ?? 0) & 0xff) |
        (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(8)) |
        (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(16)) |
        (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(24)) |
        (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(32)) |
        (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(40)) |
        (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(48)) |
        (BigInt((str.codePointAt(i++) ?? 0) & 0xff) << BigInt(56))

      k2 = k2 * m
      k2 ^= k2 >> r
      k2 = k2 * m

      h2 ^= k2
      h2 = h2 * m
    }
  }

  if (remainder > 0) {
    let k1 = BigInt(0)
    let shift = BigInt(0)

    const remainingChars = Math.min(remainder, 7)
    for (let j = 0; j < remainingChars; j++) {
      k1 |= BigInt((str.codePointAt(i + j) ?? 0) & 0xff) << shift
      shift += BigInt(8)
    }

    k1 = k1 * m
    k1 ^= k1 >> r
    k1 = k1 * m

    h1 ^= k1
  }

  h1 ^= h1 >> r
  h1 = h1 * m
  h1 ^= h1 >> r

  h2 ^= h2 >> r
  h2 = h2 * m
  h2 ^= h2 >> r

  h1 = h1 & BigInt('0xFFFFFFFFFFFFFFFF')
  h2 = h2 & BigInt('0xFFFFFFFFFFFFFFFF')

  const h1Str = h1.toString(16).padStart(16, '0')
  const h2Str = h2.toString(16).padStart(16, '0')

  return h1Str + h2Str
}
